import React from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';

const Privacy = () => {
  const title = "Orientering om behandling af personoplysninger – privatlivspolitik";
  
  const dataController = {
    title: "Dataansvarlig:",
    name: "Kay Elizabeth Worsfold, cand. psych. aut., specialist I psykoterapi.",
    cvr: "CVR nr. 34853991",
    address: "Store Kannikestræde 18, 2.th.",
    city: "1169 København K",
    email: "info@psykolog-kayworsfold.dk"
  };

  const intro = "Ifølge europæisk lovgivning fra 25. maj 2018 skal jeg informere dig om, at det i forbindelse med konsultationer her, er nødvendigt, at jeg noterer og behandler oplysninger om dig.";

  const sections = [
    {
      title: "1. Hvilke oplysninger behandler jeg?",
      paragraphs: [
        "I forbindelse med dine konsultationer på klinikken noteres der oplysninger om dine forhold til brug for den journalførelse, som jeg efter psykologloven har pligt til at foretage. De nærmere regler findes i Psykologlovens kapitel 1 og journalbekendtgørelsen (bkg. nr. 567 af 2017-05-19 om autoriserede psykologers pligt til at føre ordnede optagelser).",
        "Udover de sociale og helbredsoplysninger jeg selv noterer om dig, kan jeg modtage helbredsoplysninger om dig fra andre, f.eks. din sundhedsforsikring eller læge.",
        "Oplysninger anvendes til brug for god samtale eller behandling og de administrative funktioner, der er forbundet hermed.",
        "Jeg kan også registrere andre oplysninger om dig til brug for afregningsformål (Disse oplysninger noteres med hjemmel i databeskyttelsesforordningens art. 6, stk. 1, litra b og f. Indtil 25 maj 2018: persondatalovens § 6, styk. 1, nr. 2 og 7).",
        "Jeg har pligt til at opbevare dine helbredsmæssige og andre følsomme oplysninger sikkert og fortroligt. Jeg benytter journal-systemet EG-ClinicCare, som jeg har indgået databehandler aftale med."
      ]
    },
    {
      title: "2. Videregivelse af oplysninger",
      paragraphs: [
        "Videregivelse af helbredsoplysninger og andre personfølsomme oplysninger må som udgangspunkt kun ske med dit samtykke.",
        "I særlige tilfælde kan der efter reglerne i sundhedsloven ske videregivelse uden samtykke. Det vil typisk være til andre sundhedspersoner, f.eks. din egen læge. De nærmere regler herom findes i sundhedslovens kapitel 9.",
        "Hvis der er alvorlig risiko for skade på dig selv eller andre, har jeg oplysningspligt, for at forebygge skaden. Hvis der er alvorlig mistanke om et barns udviklingsbetingelser er i fare, har jeg underretningspligt.",
        "De oplysninger, jeg har registreret til brug for afregningsformål udveksles med betalingsformidlere i det omfang, det er nødvendigt for at gennemføre betalingerne og for revision."
      ]
    },
    {
      title: "3. Hvor længe opbevares oplysningerne?",
      paragraphs: [
        "Din journal opbevares i det tidsrum, der er fastlagt af Styrelsen for Patientsikkerhed. Det gældende tidsrum er 5 år fra seneste notat i journalen (§ 15 jf. Styk 5 og BEK nr. 567 af 19/05/2017 § 6. Den autoriserede psykolog skal opbevare en journal i mindst 5 år regnet fra tidspunktet for den senest foretagne optegnelse)."
      ]
    },
    {
      title: "4. Dine rettigheder vedrørende oplysningerne",
      paragraphs: [
        "Du kan få indsigt i hvilke oplysninger, jeg har registreret om dig ved at kontakte mig. Efter autorisationslovens § 24 må vi ikke slette oplysninger i din journal, men hvis du mener, at der er fejl i journalen, kan du bede om, at der laves en tilføjelse.",
        "For oplysninger der ikke er omfattet af patientjournalen, har du ret til at få rettet eller slettet ukorrekte oplysninger."
      ]
    },
    {
      title: "5. Klage",
      paragraphs: [
        "Hvis du er utilfreds med hvordan jeg håndterer oplysningerne om dig, vil jeg selvfølgelig gerne vide det, og om muligt ændre det, du er utilfreds med.",
        "Derudover kan klager over min behandling af dine personoplysninger indgives til Datatilsynet, Borgergade 28, 5. sal, 1300 København K. Du kan finde nærmere oplysninger om Datatilsynet på www.datatilsynet.dk"
      ]
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>{title} | Psykolog Kay Elizabeth Worsfold</title>
        <meta name="description" content="Orientering om behandling af personoplysninger og privatlivspolitik for autoriseret psykolog Kay Elizabeth Worsfold." />
      </Helmet>

      <section className="bg-stone-50 py-24">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-3xl md:text-4xl font-serif text-stone-900 mb-12 leading-tight">
              {title}
            </h1>
            
            <div className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100 mb-12">
              <h2 className="text-xl font-serif text-stone-900 mb-4">{dataController.title}</h2>
              <div className="space-y-2 text-stone-600">
                <p className="font-medium text-stone-800">{dataController.name}</p>
                <p>{dataController.cvr}</p>
                <p>{dataController.address}</p>
                <p>{dataController.city}</p>
                <p className="pt-2">
                  Mail: <a href={`mailto:${dataController.email}`} className="text-[#c16e5e] hover:underline font-medium">{dataController.email}</a>
                </p>
              </div>
            </div>

            <p className="text-lg text-stone-700 mb-12 leading-relaxed italic border-l-2 border-[#c16e5e] pl-6">
              {intro}
            </p>

            <div className="space-y-12">
              {sections.map((section, i) => (
                <div key={i} className="bg-white p-8 rounded-2xl shadow-sm border border-stone-100">
                  <h2 className="text-2xl font-serif text-stone-900 mb-6 pb-3 border-b border-stone-100">
                    {section.title}
                  </h2>
                  <div className="space-y-4 text-stone-600 leading-relaxed">
                    {section.paragraphs.map((para, pIdx) => (
                      <p key={pIdx}>{para}</p>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
